// src/components/TradeModal.jsx
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { apiUrl } from "../config/api";
import { userAuthHeaders } from "../utils/authHeaders";
import { handleUserAuthFailure } from "../utils/userPortalApi";
import { formatNumberInput, parseFormattedNumber } from "../utils/numberFormat";

function TradeModal({ isOpen, onClose, item, tabName, mode, onTradeSubmitted }) {
  const [displayWeight, setDisplayWeight] = useState("");
  const [displayPrice, setDisplayPrice] = useState("");
  const [rawWeight, setRawWeight] = useState("");
  const [rawPrice, setRawPrice] = useState("");

  useEffect(() => {
    if (isOpen) {
      setDisplayWeight("");
      setDisplayPrice("");
      setRawWeight("");
      setRawPrice("");
    }
  }, [isOpen]);

  if (!isOpen || !item) return null;

  const isOurBuy = mode === 'buy';
  const isMeltedGold = tabName === "آبشده";
  const priceValue = isOurBuy ? item.valueSell : item.valueBuy;
  
  // محاسبه نرخ هر گرم
  const pricePerGram = isOurBuy
    ? (item.valueSellPerGram || (item.valueSell ? parseFloat(item.valueSell) / 4.3318 : 0))
    : (item.valueBuyPerGram || (item.valueBuy ? parseFloat(item.valueBuy) / 4.3318 : 0));
  const tradeUnitRate = isMeltedGold ? pricePerGram : Number(priceValue || 0);

  // تغییر وزن -> محاسبه قیمت
  const handleWeightChange = (value) => {
    const convertedValue = isMeltedGold
      ? parseFormattedNumber(value, { allowDecimal: true })
      : parseFormattedNumber(value, { allowDecimal: false });
    const isValidPattern = isMeltedGold ? /^[0-9]*\.?[0-9]*$/.test(convertedValue) : /^[0-9]*$/.test(convertedValue);

    if (isValidPattern) {
      setRawWeight(convertedValue);
      setDisplayWeight(isMeltedGold ? formatNumberInput(convertedValue, { allowDecimal: true }) : convertedValue);
      if (convertedValue && parseFloat(convertedValue) > 0) {
        const total = Math.round(parseFloat(convertedValue) * tradeUnitRate);
        setRawPrice(total.toString());
        setDisplayPrice(formatNumberInput(total, { allowDecimal: false }));
      } else {
        setRawPrice("");
        setDisplayPrice("");
      }
    }
  };

  // تغییر قیمت -> محاسبه وزن
  const handlePriceChange = (value) => {
    const cleanValue = parseFormattedNumber(value, { allowDecimal: false });
    if (cleanValue === "" || /^[0-9]*\.?[0-9]*$/.test(cleanValue)) {
      setRawPrice(cleanValue);
      setDisplayPrice(formatNumberInput(cleanValue, { allowDecimal: false }));
      if (cleanValue && tradeUnitRate > 0) {
        const numValue = parseFloat(cleanValue);
        const w = numValue / tradeUnitRate;
        const nextWeight = isMeltedGold ? w.toFixed(4) : String(Math.max(1, Math.floor(w)));
        setRawWeight(nextWeight);
        setDisplayWeight(isMeltedGold ? formatNumberInput(nextWeight, { allowDecimal: true }) : nextWeight);
      } else {
        setRawWeight("");
        setDisplayWeight("");
      }
    }
  };

  const handleSubmit = async () => {
    const w = parseFloat(rawWeight);
    const p = parseFloat(rawPrice);

    if (isNaN(w) || isNaN(p) || w <= 0 || p <= 0 || (!isMeltedGold && (!Number.isInteger(w) || w < 1))) {
      toast.error(isMeltedGold ? "لطفاً وزن و مبلغ معتبر وارد کنید" : "لطفاً عدد و مبلغ معتبر وارد کنید");
      return;
    }

    let user = null;
    try {
      const userString = localStorage.getItem('user');
      if (userString) {
        user = JSON.parse(userString);
      }
    } catch (e) {
      console.error("Error parsing user from localStorage:", e);
    }

    if (!user || !user.id) {
      toast.error("لطفاً ابتدا وارد حساب کاربری شوید.");
      return;
    }

    try {
      // ساخت پیلود اولیه
      const payload = {
        userId: user.id,
        userName: user.firstName ? `${user.firstName} ${user.lastName}` : (user.username || 'کاربر مهمان'),
        itemId: item.id,
        weight: w,
        totalPrice: p,
        mode: mode
      };

      // ✅ اضافه کردن شرطی فیلد قیمت بر اساس مود
      if (mode === 'buy') {
        // برای خرید از ما، نرخ فروش ما باید ارسال شود
        payload.valueSell = item.valueSell; 
      } else if (mode === 'sell') {
        // برای فروش به ما، نرخ خرید ما باید ارسال شود
        payload.valueBuy = item.valueBuy;
      }

      const response = await fetch(apiUrl("/api/trades"), {
        method: "POST",
        headers: userAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(payload)
      });
      if (handleUserAuthFailure(response)) return;

      const result = await response.json();

      if (result.success) {
        if (onTradeSubmitted) {
          onTradeSubmitted({
            tradeId: result.tradeId,
            userId: user.id,
            weight: w,
            totalPrice: p,
            mode: mode,
            itemName: item.title,
            isMeltedGold,
            pricePerGram: pricePerGram,
            baseRate: isOurBuy ? item.valueSell : item.valueBuy,
            totalPriceValue: isOurBuy ? item.valueSell : item.valueBuy,
            priceType: isOurBuy ? 'buy' : 'sell'
          });
        }
        onClose();
        toast.success(result.message);
      } else {
        toast.error(result.message || "خطا در ثبت معامله");
      }
    } catch (error) {
      console.error("Error submitting trade:", error);
      toast.error("خطا در برقراری ارتباط با سرور");
    }
  };

  const parsedWeight = parseFloat(rawWeight);
  const isValidQuantity = isMeltedGold
    ? parsedWeight > 0
    : Number.isInteger(parsedWeight) && parsedWeight >= 1;
  const isValid = rawWeight && isValidQuantity && rawPrice && parseFloat(rawPrice) > 0;
  const titleText = isOurBuy ? "خرید از ما" : "فروش به ما";
  const priceLabel = isOurBuy ? "نرخ خرید از ما" : "نرخ فروش به ما";
  const perGramLabel = isOurBuy ? "نرخ هر گرم (خرید):" : "نرخ هر گرم (فروش):";
  const displayPricePerGram = pricePerGram;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="trade-modal" onClick={(e) => e.stopPropagation()}>
        <div className="trade-modal-header">
          <div className="trade-modal-title">
            <span className="trade-tab-name">{tabName}</span>
            <span className="trade-separator">/</span>
            <span className="trade-item-name">{item.title}</span>
          </div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="trade-price-info">
          <div className="trade-price-item">
            <span className="trade-price-label">{priceLabel}</span>
            <span className="trade-price-value" style={{ color: isOurBuy ?  "#e74c3c" : "#27ae60" }}>
              {priceValue ? Number(priceValue).toLocaleString('fa-IR') : 0} ریال
            </span>
          </div>
          <div className="trade-price-item" style={{ marginTop: "8px", opacity: 0.8, fontSize: "0.9em" }}>
            <span className="trade-price-label">{isMeltedGold ? perGramLabel : "نرخ هر عدد:"}</span>
            <span className="trade-price-value">
              {Math.round(isMeltedGold ? displayPricePerGram : Number(priceValue || 0)).toLocaleString('fa-IR')} ریال
            </span>
          </div>
        </div>

        <div className="trade-form">
          <div className="trade-input-group">
            <label>{isMeltedGold ? "وزن (گرم)" : "عدد"}</label>
            <input
              type={isMeltedGold ? "text" : "number"}
              min={isMeltedGold ? undefined : 1}
              step={isMeltedGold ? undefined : 1}
              inputMode={isMeltedGold ? "decimal" : "numeric"}
              value={displayWeight}
              onChange={(e) => handleWeightChange(e.target.value)}
              onBlur={() => {
                if (!isMeltedGold && rawWeight !== "" && Number(rawWeight) < 1) {
                  handleWeightChange("1");
                }
              }}
              placeholder={isMeltedGold ? "مثال: 10" : "مثال: 1"}
              className="trade-input"
            />
          </div>
          <div className="trade-input-group">
            <label>مبلغ کل (ریال)</label>
            <input
              type="text"
              value={displayPrice}
              onChange={(e) => handlePriceChange(e.target.value)}
              placeholder="مثال: 100,000,000"
              className="trade-input"
            />
          </div>
        </div>

        <button
          className={`trade-submit-btn ${isValid ? 'active' : 'disabled'}`}
          style={{ backgroundColor: "#ffcc00" }}
          onClick={handleSubmit}
          disabled={!isValid}
        >
          تأیید
        </button>
      </div>
    </div>,
    document.body
  );
}

export default TradeModal;
